/** Contractor agreement — the form (Tom 2026-09-04). Opens from a deal's
 * Contractors tab with the project address, dates, price and vendor list
 * already filled; Tom adjusts, picks the payment split (5×20 / 4×25 / 3×33 or
 * anything he types), sees the exact money wording and installment sentence,
 * and downloads the finished Word file. A W-9 upload fills the contractor's
 * name and address ("upload w9 and it will take the info of the contractor"). */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Contact } from "../contacts/api";
import {
  BUILTIN_DEFAULTS, SPLIT_PRESETS, downloadBlob, fmtMoney, generateAgreement, getAgreementDefaults,
  moneyWords, parseMoney, parseSplit, parseW9, saveAgreementDefaults, splitSentence, toIsoDate,
  type AgreementDefaults, type AgreementValues,
} from "./api";

export interface AgreementSeed {
  projectAddress: string;
  price?: number | null;
  startDate?: string | null;       // any date text the deal holds
  completionDate?: string | null;
  contractorName?: string | null;
  contractorAddress?: string | null;
}

interface VendorOpt { label: string; name: string; address: string }

/** Contacts that are contractors/vendors (same rule as the expense form) with
 * the address the contact card holds, joined city/state/zip. */
export function vendorOptions(contacts: Contact[]): VendorOpt[] {
  const out: VendorOpt[] = [];
  for (const c of contacts) {
    const ct = typeof c.raw["contractorType"] === "string" ? (c.raw["contractorType"] as string) : "";
    const isVendor = !!ct || c.raw["r"] === "Contractor" || c.tags.some((t) => t === "Vendor" || t === "Contractor");
    if (!isVendor) continue;
    const s = (k: string) => (typeof c.raw[k] === "string" ? (c.raw[k] as string).trim() : "");
    const csz = [s("city"), [s("state"), s("zip")].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    const address = [c.address?.trim() ?? "", csz].filter(Boolean).join(", ");
    out.push({ label: c.company ? `${c.company} — ${c.name}` : c.name, name: c.company || c.name, address });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function ContractorAgreementModal({ token, isAdmin, seed, contacts, onClose }: {
  token: string | null;
  isAdmin: boolean;
  seed: AgreementSeed;
  contacts: Contact[];
  onClose: () => void;
}) {
  const [defaults, setDefaults] = useState<AgreementDefaults>(BUILTIN_DEFAULTS);
  const [v, setV] = useState<AgreementValues>(() => ({
    effectiveDate: todayIso(),
    projectAddress: seed.projectAddress,
    price: seed.price ? String(seed.price) : "",
    startDate: toIsoDate(seed.startDate),
    completionDate: toIsoDate(seed.completionDate),
    contractorName: seed.contractorName ?? "",
    contractorAddress: seed.contractorAddress ?? "",
    ...BUILTIN_DEFAULTS,
  }));
  const [splitText, setSplitText] = useState(BUILTIN_DEFAULTS.split.join(" / "));
  const [busy, setBusy] = useState<"" | "gen" | "w9" | "defaults">("");
  const [note, setNote] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const w9Ref = useRef<HTMLInputElement>(null);
  const vendors = useMemo(() => vendorOptions(contacts), [contacts]);

  // org defaults (owner, LD, lender, venue, split) — seed once, deal values win
  useEffect(() => {
    let alive = true;
    void getAgreementDefaults(token).then((d) => {
      if (!alive) return;
      setDefaults(d);
      setV((p) => ({ ...p, ownerName: d.ownerName, ownerAddress: d.ownerAddress, ldPerDay: d.ldPerDay,
        startGraceDays: d.startGraceDays, lender: d.lender, venueCounty: d.venueCounty, split: d.split }));
      setSplitText(d.split.join(" / "));
    }).catch(() => { /* built-ins stay */ });
    return () => { alive = false; };
  }, [token]);

  const set = (k: keyof AgreementValues, val: string) => setV((p) => ({ ...p, [k]: val }));

  const price = parseMoney(v.price);
  const parsed = useMemo(() => parseSplit(splitText), [splitText]);
  const split = "split" in parsed ? parsed.split : null;
  const splitErr = "error" in parsed ? parsed.error : null;
  useEffect(() => { if (split) setV((p) => ({ ...p, split })); }, [split]);

  const missing: string[] = [];
  if (!v.contractorName?.trim()) missing.push("contractor name");
  if (!v.contractorAddress?.trim()) missing.push("contractor address");
  if (!v.projectAddress?.trim()) missing.push("project address");
  if (!(price > 0)) missing.push("contract price");
  if (splitErr) missing.push("payment split");

  const pickVendor = (i: string) => {
    const o = vendors[Number(i)];
    if (!o) return;
    setV((p) => ({ ...p, contractorName: o.name, contractorAddress: o.address || p.contractorAddress || "" }));
  };

  const onW9 = async (file: File) => {
    setBusy("w9"); setNote(null);
    try {
      const info = await parseW9(token, file);
      setV((p) => ({ ...p, contractorName: info.contractorName || p.contractorName || "",
        contractorAddress: info.contractorAddress || p.contractorAddress || "" }));
      const bits = [info.businessName ? `business name "${info.businessName}"` : `name "${info.name}"`, info.contractorAddress ? "address" : ""].filter(Boolean);
      setNote({ kind: "ok", text: `W-9 read (${info.how}): filled ${bits.join(" + ")}. Check it, then generate.` });
    } catch (e) {
      setNote({ kind: "err", text: (e as { detail?: string })?.detail ?? (e as Error)?.message ?? "Couldn't read the W-9" });
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    if (busy || missing.length) {
      if (missing.length) setNote({ kind: "err", text: "Still needed: " + missing.join(", ") + "." });
      return;
    }
    setBusy("gen"); setNote(null);
    try {
      const { blob, filename } = await generateAgreement(token, v);
      downloadBlob(blob, filename);
      setNote({ kind: "ok", text: `Downloaded ${filename} — open it in Word or Google Docs to review and send.` });
    } catch (e) {
      setNote({ kind: "err", text: (e as { detail?: string })?.detail ?? (e as Error)?.message ?? "Generate failed" });
    } finally {
      setBusy("");
    }
  };

  const saveDefaults = async () => {
    setBusy("defaults"); setNote(null);
    try {
      const d: AgreementDefaults = {
        ownerName: v.ownerName || defaults.ownerName, ownerAddress: v.ownerAddress || defaults.ownerAddress,
        ldPerDay: v.ldPerDay || defaults.ldPerDay, startGraceDays: v.startGraceDays || defaults.startGraceDays,
        lender: v.lender || defaults.lender, venueCounty: v.venueCounty || defaults.venueCounty,
        split: split ?? defaults.split,
      };
      await saveAgreementDefaults(token, d);
      setDefaults(d);
      setNote({ kind: "ok", text: "Saved as the defaults for every new agreement." });
    } catch (e) {
      setNote({ kind: "err", text: (e as { detail?: string })?.detail ?? (e as Error)?.message ?? "Save failed" });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="modalscrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal fbmodal camodal" role="dialog" aria-modal="true" aria-label="Contractor agreement">
        <div className="modalhd">
          <h2>📄 Contractor Agreement</h2>
          <button className="modalx" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="casec">
          <div className="casechd">
            <span>Contractor</span>
            <span className="casecacts">
              {vendors.length ? (
                <select className="moneyinput" value="" onChange={(e) => pickVendor(e.target.value)} aria-label="Pick a vendor from Contacts">
                  <option value="">Pick from Contacts…</option>
                  {vendors.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
                </select>
              ) : null}
              <button className="btn slim" disabled={busy !== ""} onClick={() => w9Ref.current?.click()}>
                {busy === "w9" ? "Reading W-9…" : "⬆ Fill from W-9"}
              </button>
              <input ref={w9Ref} type="file" accept="application/pdf,.pdf" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void onW9(f); e.target.value = ""; }} />
            </span>
          </div>
          <div className="fbgrid">
            <label>Contractor (legal / business name)<input value={v.contractorName ?? ""} placeholder="SeaState Land Services Company LLC" onChange={(e) => set("contractorName", e.target.value)} /></label>
            <label>Contractor address<input value={v.contractorAddress ?? ""} placeholder="1251 Sarasota Center Blvd, Sarasota, FL 34240" onChange={(e) => set("contractorAddress", e.target.value)} /></label>
          </div>
        </div>

        <div className="casec">
          <div className="casechd"><span>Project &amp; dates</span></div>
          <div className="fbgrid">
            <label className="wide">Project address<input value={v.projectAddress ?? ""} onChange={(e) => set("projectAddress", e.target.value)} /></label>
            <label>Effective date<input type="date" value={v.effectiveDate ?? ""} onChange={(e) => set("effectiveDate", e.target.value)} /></label>
            <label>Start date<input type="date" value={v.startDate ?? ""} onChange={(e) => set("startDate", e.target.value)} /></label>
            <label>Completion date<input type="date" value={v.completionDate ?? ""} onChange={(e) => set("completionDate", e.target.value)} /></label>
            <label>Days to begin after start<input inputMode="numeric" value={v.startGraceDays ?? ""} onChange={(e) => set("startGraceDays", e.target.value)} /></label>
          </div>
        </div>

        <div className="casec">
          <div className="casechd"><span>Exhibit B — Scope of Work</span><span className="sub">attached to the agreement, initialed by both sides</span></div>
          <div className="fbgrid">
            <label>Scope of Work date<input type="date" value={v.sowDate ?? ""} onChange={(e) => set("sowDate", e.target.value)} /></label>
            <label>Pages<input inputMode="numeric" placeholder="left blank to fill by hand" value={v.sowPages ?? ""} onChange={(e) => set("sowPages", e.target.value)} /></label>
          </div>
          {!v.sowDate ? <div className="sub" style={{ marginTop: 6 }}>No date → the effective date is used.</div> : null}
        </div>

        <div className="casec">
          <div className="casechd"><span>Contract price &amp; payments</span></div>
          <div className="fbgrid">
            <label>Contract price ($)<input inputMode="decimal" value={v.price ?? ""} placeholder="52,761.42" onChange={(e) => set("price", e.target.value)} /></label>
            <label>Liquidated damages per day ($)<input inputMode="decimal" value={v.ldPerDay ?? ""} onChange={(e) => set("ldPerDay", e.target.value)} /></label>
          </div>
          {price > 0 ? <div className="cawords">{fmtMoney(price)} ({moneyWords(price)})</div> : null}
          <div className="casplit">
            <div className="fld-l">Payment split</div>
            <div className="casplitpre">
              {SPLIT_PRESETS.map((p) => (
                <button key={p.id} type="button"
                  className={"btn slim" + (split && split.join("/") === p.split.join("/") ? " primary" : "")}
                  onClick={() => setSplitText(p.split.join(" / "))}>{p.label}</button>
              ))}
              <input className="moneyinput casplitin" value={splitText} placeholder="e.g. 10 / 30 / 30 / 30"
                onChange={(e) => setSplitText(e.target.value)} aria-label="Custom split percentages" />
            </div>
            {splitErr ? <div className="caerr">{splitErr}</div> : null}
            {split && price > 0 ? (
              <div className="capreview">
                <b>{split.length} payment{split.length === 1 ? "" : "s"}:</b> {splitSentence(price, split)}
              </div>
            ) : split ? <div className="sub">Enter the price to see the installment amounts.</div> : null}
          </div>
        </div>

        <div className="casec">
          <button type="button" className="casechd castoggle" onClick={() => setTermsOpen((o) => !o)}>
            <span>Owner &amp; terms</span><span className="sub">{v.ownerName} · lender {v.lender} · venue: the Property's county {termsOpen ? "▴" : "▾"}</span>
          </button>
          {termsOpen ? (
            <div className="fbgrid">
              <label>Owner (your company)<input value={v.ownerName ?? ""} onChange={(e) => set("ownerName", e.target.value)} /></label>
              <label>Owner address<input value={v.ownerAddress ?? ""} onChange={(e) => set("ownerAddress", e.target.value)} /></label>
              <label>Lender (§19)<input value={v.lender ?? ""} onChange={(e) => set("lender", e.target.value)} /></label>
            </div>
          ) : null}
        </div>

        <div className="modalacts">
          <button className={"btn primary" + (missing.length ? " soft" : "")} disabled={busy !== ""} onClick={() => void generate()}>
            {busy === "gen" ? "Building the Word file…" : "Generate & download .docx"}
          </button>
          <button className="btn" onClick={onClose}>Close</button>
          {isAdmin ? <button className="btn ghost" disabled={busy !== ""} onClick={() => void saveDefaults()}>Save owner/terms/split as defaults</button> : null}
        </div>
        {missing.length && !note ? <div className="sub" style={{ marginTop: 8 }}>Still needed: {missing.join(", ")}.</div> : null}
        {note ? <div className={note.kind === "err" ? "caerr" : "fbnote"} role={note.kind === "err" ? "alert" : undefined}>{note.text}</div> : null}
      </div>
    </div>
  );
}
